import * as React from 'react';
import PropTypes from 'prop-types';
import firebase from 'firebase';
import { withStyles } from '@material-ui/core/styles';
import {
  Button,
  IconButton,
  MenuItem,
  Paper,
  Radio,
  RadioGroup,
  FormControlLabel,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@material-ui/core';
import DeleteIcon from '@material-ui/icons/Delete';
import EditIcon from '@material-ui/icons/Edit';
import AddIcon from '@material-ui/icons/AddCircleOutline';
import { gradeSpanLabel, spanStringLabel } from './gradeLabels';

const styles = (theme) => ({
  root: {
    marginTop: 32,
    maxWidth: 1100,
  },
  intro: {
    marginBottom: 16,
    color: theme.palette.text.secondary,
  },
  row: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '8px 16px',
    marginBottom: 8,
  },
  form: {
    padding: 16,
    marginTop: 16,
    marginBottom: 16,
  },
  memberCard: {
    padding: 12,
    margin: '12px 0',
    background: '#fafafa',
  },
  memberRow: {
    display: 'flex',
    alignItems: 'center',
  },
  campusField: {
    minWidth: 240,
    marginRight: 16,
  },
  gradeField: {
    minWidth: 100,
    marginRight: 16,
  },
  identityField: {
    minWidth: 280,
    marginRight: 16,
  },
  actions: {
    marginTop: 16,
    display: 'flex',
    alignItems: 'center',
  },
  spaced: {
    marginLeft: 8,
  },
  error: {
    color: theme.palette.error.main,
    marginTop: 4,
  },
  warning: {
    color: '#b26a00',
    marginTop: 4,
  },
  rationale: {
    color: theme.palette.text.secondary,
    fontSize: 12,
  },
  results: {
    padding: 16,
    marginTop: 16,
    overflowX: 'auto',
  },
  districtRow: {
    background: '#eef5f9',
  },
  finePrint: {
    marginTop: 16,
    padding: 12,
    background: '#f5f5f5',
    fontSize: 12,
    color: theme.palette.text.secondary,
    whiteSpace: 'pre-line',
  },
});

const WHOLE = '';

const FINE_PRINT = `About these projections

This scenario is a what-if calculation built from your district's own student data. TEA has not reviewed or rated it, and nothing here changes any official rating.

Which campus number continues after a reconfiguration is your district's decision, made through AskTED. A campus that keeps its number also keeps its prior accountability record exactly as TEA stored it, even when the students it serves change (2026 Accountability Manual, Chapter 9). A campus given a new number is treated as brand new: it is measured against the state's lowest performing student groups, it can only earn full, interim, or zero credit on Closing the Gaps indicators, it has no growth baselines, and it still receives a full A-F rating in its first year (2026 Accountability Manual, Chapter 4). There is no grace year. A district that merged two campuses under a new number, for example, should expect that campus to be rated immediately on partial data.

If any campus involved is rated D, F, or Not Rated, or is identified for federal school improvement, number changes require review by TEA, and the commissioner can deny a change that would sidestep required interventions (19 TAC §97.1066; 2026 Accountability Manual, Chapter 9). TEA can also carry a closed campus's rating history over to the campus that receives its students. Number changes take effect the following school year.

These projections use only the students in your uploaded data files. Reconstructed prior year baselines come from the prior year scores carried in your current year files, which cover only students still enrolled in the district. Students who have left are not included, so campuses with high mobility or significant enrollment changes may see actual results that differ from these projections.

These projections show what current data suggests under each choice. TEA makes the actual determinations. This summary reflects TEA rules as of August 2026 and is for planning purposes only. It is not legal advice; consult your district's counsel and TEA before acting on a reconfiguration.`;

const TREATMENT_LABELS = {
  recorded: 'uses recorded prior-year data',
  firstYear: 'scored as a first-year campus',
  reconstructed: 'prior year reconstructed from this arrangement',
};

const gradeOptionsForCampus = (school) => {
  if (!school) return [];
  const low = parseInt(school.gradeStart, 10);
  const high = parseInt(school.gradeEnd, 10);
  if (isNaN(high)) return [];
  const start = isNaN(low) ? 1 : low;
  const options = [];
  for (let g = start; g <= high; g += 1) options.push(g);
  return options;
};

const emptySegment = () => ({ campusNumber: '', gradeStart: WHOLE, gradeEnd: WHOLE });
const emptyMember = () => ({
  campusName: '',
  segments: [emptySegment()],
  identityChoice: 'suggested',
  identityCampus: '',
  identityNumber: '',
});

class ScenarioManager extends React.PureComponent {
  static propTypes = {
    classes: PropTypes.any.isRequired,
    profile: PropTypes.object,
    schoolList: PropTypes.object.isRequired,
    filters: PropTypes.object,
  };

  state = {
    scenarios: null,
    editingId: null, // null = closed, '' = new
    name: '',
    d3Mode: 'teaIdentity',
    members: [emptyMember()],
    preview: null, // {errors, warnings, keepCampuses, suggestions}
    busy: false,
    running: false,
    error: '',
    results: null,
  };

  componentDidMount() {
    this.loadScenarios();
  }

  loadScenarios = async () => {
    try {
      const res = await firebase.functions().httpsCallable('getScenarios')({});
      this.setState({ scenarios: (res.data && res.data.scenarios) || {} });
    } catch (e) {
      this.setState({ scenarios: {}, error: e.message });
    }
  };

  campusChoices() {
    const { schoolList, profile } = this.props;
    return Object.entries(schoolList || {})
      .filter(([campusNumber]) => campusNumber !== (profile && profile.districtNumber))
      .map(([campusNumber, school]) => ({ campusNumber, school }))
      .sort((a, b) => (a.school.name || '').localeCompare(b.school.name || ''));
  }

  // State indices of members that actually get sent (must use the same
  // predicate as toWireMembers so preview suggestions map back to the right
  // cards even when empty draft cards sit between real ones).
  wireIndexMap = () =>
    this.state.members.map((m, i) => (m.segments.some((s) => s.campusNumber) ? i : null)).filter((i) => i !== null);

  // A one-sided grade range would silently send a whole-campus segment (the
  // phase-1 group editor had this exact bug class) — block it up front.
  oneSidedError = () => {
    const oneSided = this.state.members.some((m) =>
      m.segments.some((s) => s.campusNumber && (s.gradeStart === WHOLE) !== (s.gradeEnd === WHOLE))
    );
    return oneSided ? 'Set both From and To grades, or leave both as "Whole campus".' : null;
  };

  toWireMembers = () =>
    this.state.members
      .filter((m) => m.segments.some((s) => s.campusNumber))
      .map((m) => ({
        campusName: m.campusName,
        memberSegments: m.segments
          .filter((s) => s.campusNumber)
          .map((s) => ({
            campusNumber: s.campusNumber,
            ...(s.gradeStart !== WHOLE && s.gradeEnd !== WHOLE
              ? { gradeStart: Number(s.gradeStart), gradeEnd: Number(s.gradeEnd) }
              : {}),
          })),
        ...(m.identityChoice === 'continues' && m.identityCampus
          ? { teaIdentity: { mode: 'continues', campusNumber: m.identityCampus } }
          : m.identityChoice === 'new'
          ? { teaIdentity: { mode: 'new', ...(m.identityNumber ? { hypotheticalNumber: m.identityNumber } : {}) } }
          : {}),
      }));

  runPreview = async () => {
    const oneSided = this.oneSidedError();
    if (oneSided) {
      this.setState({ error: oneSided });
      return;
    }
    this.setState({ busy: true, error: '' });
    try {
      const res = await firebase.functions().httpsCallable('previewScenario')({
        members: this.toWireMembers(),
        schoolYear: (this.props.filters && this.props.filters.schoolYear) || undefined,
      });
      this.setState({ busy: false, preview: { ...res.data, indexMap: this.wireIndexMap() } });
    } catch (e) {
      this.setState({ busy: false, error: e.message });
    }
  };

  openNew = () => {
    this.setState({
      editingId: '',
      name: '',
      d3Mode: 'teaIdentity',
      members: [emptyMember()],
      preview: null,
      error: '',
      results: null,
    });
  };

  openEdit = (scenarioId, scenario) => {
    const members = (scenario.members || []).map((member) => ({
      campusName: member.campusName || '',
      segments: (member.memberSegments || []).map((s) => ({
        campusNumber: s.campusNumber,
        gradeStart: s.gradeStart != null ? String(s.gradeStart) : WHOLE,
        gradeEnd: s.gradeEnd != null ? String(s.gradeEnd) : WHOLE,
      })),
      identityChoice: member.teaIdentity ? member.teaIdentity.mode : 'suggested',
      identityCampus: (member.teaIdentity && member.teaIdentity.campusNumber) || '',
      identityNumber: (member.teaIdentity && member.teaIdentity.hypotheticalNumber) || '',
    }));
    this.setState({
      editingId: scenarioId,
      name: scenario.name || '',
      d3Mode: scenario.d3Mode === 'steadyState' ? 'steadyState' : 'teaIdentity',
      members: members.length ? members : [emptyMember()],
      preview: null,
      error: '',
      results: null,
    });
  };

  save = async () => {
    const oneSided = this.oneSidedError();
    if (oneSided) {
      this.setState({ error: oneSided });
      return null;
    }
    const { editingId, name, d3Mode } = this.state;
    this.setState({ busy: true, error: '' });
    try {
      const payload = { name, d3Mode, members: this.toWireMembers() };
      if (editingId) payload.scenarioId = editingId;
      const res = await firebase.functions().httpsCallable('saveScenario')(payload);
      this.setState({
        busy: false,
        editingId: res.data.scenarioId,
        preview: {
          errors: res.data.errors || [],
          warnings: res.data.warnings || [],
          suggestions: (this.state.preview && this.state.preview.suggestions) || [],
          indexMap: (this.state.preview && this.state.preview.indexMap) || this.wireIndexMap(),
        },
      });
      this.loadScenarios();
      return res.data.scenarioId;
    } catch (e) {
      this.setState({ busy: false, error: e.message });
      return null;
    }
  };

  delete = (scenarioId, scenarioName) => async () => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Delete scenario "${scenarioName}"?`)) return;
    try {
      await firebase.functions().httpsCallable('deleteScenario')({ scenarioId });
      this.loadScenarios();
    } catch (e) {
      this.setState({ error: e.message });
    }
  };

  // Save-then-run: the backend always executes the SAVED scenario, so
  // persisting first guarantees the run matches what's on screen.
  run = async () => {
    const { filters, assessmentGroups, assessmentList } = this.props;
    const scenarioId = await this.save();
    if (!scenarioId) return;
    const schoolYear = (filters && filters.schoolYear) || null;
    // Mirror the A-F page's assessment resolution: a combined assessment
    // group selection stores the GROUP id in assessmentSource and its set
    // ids live under assessmentGroups.
    let sourceIds = (filters && filters.assessmentSource) || [];
    if (filters && filters.assessmentType === 'group' && typeof sourceIds === 'string') {
      const group = (assessmentGroups || {})[sourceIds];
      sourceIds = (group && group.assessments) || [];
    }
    if (!Array.isArray(sourceIds)) sourceIds = [sourceIds].filter(Boolean);
    if (!schoolYear || !sourceIds.length) {
      this.setState({
        error:
          'Pick a school year and assessment source on the A-F Reports page first; the scenario runs with that selection.',
      });
      return;
    }
    this.setState({ running: true, error: '', results: null });
    try {
      const assessmentsInfo = (assessmentList || [])
        .filter((a) => sourceIds.includes(a.id))
        .map((a) => ({ id: a.id, type: a.type }));
      const res = await firebase.functions().httpsCallable('runScenario', { timeout: 540000 })({
        scenarioId,
        schoolYear,
        assessmentsInfo,
      });
      this.setState({ running: false, results: res.data });
    } catch (e) {
      this.setState({ running: false, error: e.message });
    }
  };

  updateMember(index, patch) {
    const members = this.state.members.slice();
    members[index] = { ...members[index], ...patch };
    this.setState({ members, preview: null, results: null });
  }

  updateSegment(memberIndex, segmentIndex, field, value) {
    const members = this.state.members.slice();
    const segments = members[memberIndex].segments.slice();
    segments[segmentIndex] = { ...segments[segmentIndex], [field]: value };
    if (field === 'campusNumber') {
      segments[segmentIndex].gradeStart = WHOLE;
      segments[segmentIndex].gradeEnd = WHOLE;
    }
    members[memberIndex] = { ...members[memberIndex], segments };
    this.setState({ members, preview: null, results: null });
  }

  renderSegmentRow(memberIndex, segment, segmentIndex) {
    const { classes, schoolList } = this.props;
    const school = (schoolList || {})[segment.campusNumber];
    const grades = gradeOptionsForCampus(school);
    return (
      <div className={classes.memberRow} key={segmentIndex}>
        <TextField
          select
          label="Campus"
          className={classes.campusField}
          value={segment.campusNumber}
          onChange={(e) => this.updateSegment(memberIndex, segmentIndex, 'campusNumber', e.target.value)}
          margin="dense"
        >
          {this.campusChoices().map(({ campusNumber, school: choice }) => (
            <MenuItem key={campusNumber} value={campusNumber}>
              {choice.name} (
              {gradeSpanLabel(choice.gradeStart, choice.gradeEnd) || `${choice.gradeStart}-${choice.gradeEnd}`})
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          label="From grade"
          className={classes.gradeField}
          value={segment.gradeStart}
          onChange={(e) => this.updateSegment(memberIndex, segmentIndex, 'gradeStart', e.target.value)}
          margin="dense"
          disabled={!grades.length}
        >
          <MenuItem value={WHOLE}>Whole campus</MenuItem>
          {grades.map((g) => (
            <MenuItem key={g} value={String(g)}>
              {g}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          label="To grade"
          className={classes.gradeField}
          value={segment.gradeEnd}
          onChange={(e) => this.updateSegment(memberIndex, segmentIndex, 'gradeEnd', e.target.value)}
          margin="dense"
          disabled={!grades.length || segment.gradeStart === WHOLE}
        >
          <MenuItem value={WHOLE}>Whole campus</MenuItem>
          {grades
            .filter((g) => segment.gradeStart === WHOLE || g >= Number(segment.gradeStart))
            .map((g) => (
              <MenuItem key={g} value={String(g)}>
                {g}
              </MenuItem>
            ))}
        </TextField>
        <IconButton
          onClick={() => {
            const members = this.state.members.slice();
            const segments = members[memberIndex].segments.filter((s, i) => i !== segmentIndex);
            members[memberIndex] = { ...members[memberIndex], segments: segments.length ? segments : [emptySegment()] };
            this.setState({ members, preview: null, results: null });
          }}
          title="Remove"
        >
          <DeleteIcon fontSize="small" />
        </IconButton>
      </div>
    );
  }

  renderMemberCard(member, index) {
    const { classes } = this.props;
    const { preview } = this.state;
    // Suggestions are positional over the SENT members; map this card's
    // state index back through the wire index map.
    const sentPosition = preview && preview.indexMap ? preview.indexMap.indexOf(index) : index;
    const suggestion = preview && preview.suggestions && sentPosition >= 0 ? preview.suggestions[sentPosition] : null;
    return (
      <Paper key={index} className={classes.memberCard}>
        <div className={classes.memberRow}>
          <TextField
            label="Hypothetical campus name"
            value={member.campusName}
            onChange={(e) => this.updateMember(index, { campusName: e.target.value })}
            margin="dense"
            style={{ minWidth: 280, marginRight: 16 }}
          />
          <TextField
            select
            label="TEA identity"
            className={classes.identityField}
            value={member.identityChoice}
            onChange={(e) => this.updateMember(index, { identityChoice: e.target.value })}
            margin="dense"
          >
            <MenuItem value="suggested">Auto-suggest</MenuItem>
            <MenuItem value="continues">Continues as an existing campus</MenuItem>
            <MenuItem value="new">New campus to TEA</MenuItem>
          </TextField>
          {member.identityChoice === 'continues' && (
            <TextField
              select
              label="Continues as"
              className={classes.identityField}
              value={member.identityCampus}
              onChange={(e) => this.updateMember(index, { identityCampus: e.target.value })}
              margin="dense"
            >
              {this.campusChoices().map(({ campusNumber, school: choice }) => (
                <MenuItem key={campusNumber} value={campusNumber}>
                  {choice.name} ({campusNumber})
                </MenuItem>
              ))}
            </TextField>
          )}
          {member.identityChoice === 'new' && (
            <TextField
              label="Hypothetical number"
              value={member.identityNumber}
              onChange={(e) => this.updateMember(index, { identityNumber: e.target.value })}
              margin="dense"
              style={{ minWidth: 160 }}
            />
          )}
          <IconButton
            onClick={() => {
              const members = this.state.members.filter((m, i) => i !== index);
              this.setState({ members: members.length ? members : [emptyMember()], preview: null, results: null });
            }}
            title="Remove campus"
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </div>
        {suggestion && (
          <Typography className={classes.rationale}>
            Suggested:{' '}
            {suggestion.mode === 'continues'
              ? `continues as ${suggestion.campusNumber}`
              : `new campus${suggestion.hypotheticalNumber ? ` (${suggestion.hypotheticalNumber})` : ''}`}
            {suggestion.rationale ? ` — ${suggestion.rationale}` : ''}
          </Typography>
        )}
        {member.segments.map((segment, segmentIndex) => this.renderSegmentRow(index, segment, segmentIndex))}
        <Button
          color="primary"
          size="small"
          onClick={() => {
            const members = this.state.members.slice();
            members[index] = { ...members[index], segments: [...members[index].segments, emptySegment()] };
            this.setState({ members, preview: null, results: null });
          }}
        >
          <AddIcon fontSize="small" />
          &nbsp;Add campus or grade range
        </Button>
      </Paper>
    );
  }

  renderResults() {
    const { classes } = this.props;
    const { results } = this.state;
    if (!results) return null;
    const cell = (v) => (v == null || Number.isNaN(v) ? '-' : v);
    const rows = [
      ...results.members.map((m) => ({
        label: `${m.campusName} (${spanStringLabel(m.gradeSpan) || m.gradeSpan}, ${m.schoolType})`,
        sub: [
          m.identity.mode === 'continues'
            ? `continues as ${m.identity.campusNumber}`
            : `new to TEA${m.identity.hypotheticalNumber ? ` (${m.identity.hypotheticalNumber})` : ''}`,
          TREATMENT_LABELS[m.d3PriorYearTreatment]
            ? `Closing the Gaps ${TREATMENT_LABELS[m.d3PriorYearTreatment]}`
            : null,
          m.ecoDis != null ? `Eco Dis ${m.ecoDis}%` : null,
          m.reportError || null,
        ]
          .filter(Boolean)
          .join('; '),
        s: m.summary || {},
        weight: m.weight && m.weight.total,
      })),
      ...results.keepCampuses.map((k) => ({
        label: `${k.campusName} (unchanged)`,
        sub: k.reportError ? k.reportError : '',
        s: k.summary || {},
        weight: k.weight && k.weight.total,
      })),
    ];
    return (
      <Paper className={classes.results}>
        <Typography variant="h6">Scenario results</Typography>
        {results.scenario.d3Mode === 'steadyState' && (
          <Typography className={classes.rationale}>
            Steady state: prior-year baselines reconstructed from this arrangement where possible.
          </Typography>
        )}
        <Table padding="dense">
          <TableHead>
            <TableRow>
              <TableCell>Campus</TableCell>
              <TableCell align="right">Students (3-12)</TableCell>
              <TableCell align="right">Student Achievement</TableCell>
              <TableCell align="right">Academic Growth</TableCell>
              <TableCell align="right">Relative Performance</TableCell>
              <TableCell align="right">Closing the Gaps</TableCell>
              <TableCell align="right">Overall</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row) => (
              <TableRow key={row.label}>
                <TableCell>
                  {row.label}
                  {row.sub ? <div className={classes.rationale}>{row.sub}</div> : null}
                </TableCell>
                <TableCell align="right">{cell(row.weight)}</TableCell>
                <TableCell align="right">{cell(row.s.d1)}</TableCell>
                <TableCell align="right">{cell(row.s.d2a)}</TableCell>
                <TableCell align="right">{cell(row.s.d2b)}</TableCell>
                <TableCell align="right">{cell(row.s.d3)}</TableCell>
                <TableCell align="right">
                  {cell(row.s.score)} {row.s.letter ? `(${row.s.letter})` : ''}
                </TableCell>
              </TableRow>
            ))}
            <TableRow className={classes.districtRow}>
              <TableCell>District under this scenario</TableCell>
              <TableCell align="right" />
              <TableCell align="right">{cell(results.scenarioDistrict.d1)}</TableCell>
              <TableCell align="right">{cell(results.scenarioDistrict.d2a)}</TableCell>
              <TableCell align="right">{cell(results.scenarioDistrict.d2b)}</TableCell>
              <TableCell align="right">{cell(results.scenarioDistrict.d3)}</TableCell>
              <TableCell align="right">
                {cell(results.scenarioDistrict.score)} ({results.scenarioDistrict.letter})
              </TableCell>
            </TableRow>
            {results.currentDistrict && (
              <TableRow className={classes.districtRow}>
                <TableCell>District today (actual configuration)</TableCell>
                <TableCell align="right" />
                <TableCell align="right">{cell(results.currentDistrict.d1)}</TableCell>
                <TableCell align="right">{cell(results.currentDistrict.d2a)}</TableCell>
                <TableCell align="right">{cell(results.currentDistrict.d2b)}</TableCell>
                <TableCell align="right">{cell(results.currentDistrict.d3)}</TableCell>
                <TableCell align="right">
                  {cell(results.currentDistrict.score)} ({results.currentDistrict.letter})
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
        {(results.members || [])
          .filter((m) => m.d3SteadyStateNotices && m.d3SteadyStateNotices.length)
          .map((m) => (
            <Typography key={m.campusName} className={classes.rationale}>
              {m.campusName}: {m.d3SteadyStateNotices.join(' ')}
            </Typography>
          ))}
        <div className={classes.finePrint}>{FINE_PRINT}</div>
      </Paper>
    );
  }

  renderEditor() {
    const { classes } = this.props;
    const { editingId, name, members, d3Mode, preview, busy, running, error } = this.state;
    if (editingId === null) return null;
    return (
      <Paper className={classes.form}>
        <Typography variant="h6">{editingId ? 'Edit Scenario' : 'New Scenario'}</Typography>
        <TextField
          label="Scenario name"
          value={name}
          onChange={(e) => this.setState({ name: e.target.value })}
          margin="dense"
          style={{ minWidth: 320 }}
        />
        <RadioGroup row value={d3Mode} onChange={(e) => this.setState({ d3Mode: e.target.value })}>
          <FormControlLabel
            value="teaIdentity"
            control={<Radio color="primary" />}
            label="What happens next year (TEA identity)"
          />
          <FormControlLabel
            value="steadyState"
            control={<Radio color="primary" />}
            label="Once established (reconstructed prior year)"
          />
        </RadioGroup>
        {members.map((member, index) => this.renderMemberCard(member, index))}
        <Button
          color="primary"
          size="small"
          onClick={() => this.setState({ members: [...members, emptyMember()], preview: null, results: null })}
        >
          <AddIcon fontSize="small" />
          &nbsp;Add hypothetical campus
        </Button>
        {preview &&
          (preview.errors || []).map((e) => (
            <Typography key={e} className={classes.error}>
              {e}
            </Typography>
          ))}
        {preview &&
          (preview.warnings || []).map((w) => (
            <Typography key={w} className={classes.warning}>
              {w}
            </Typography>
          ))}
        {preview && preview.keepCampuses && preview.keepCampuses.length > 0 && (
          <Typography className={classes.rationale}>
            Unchanged campuses:{' '}
            {preview.keepCampuses.map((c) => ((this.props.schoolList || {})[c] || {}).name || c).join(', ')}
          </Typography>
        )}
        <div className={classes.actions}>
          <Button onClick={this.runPreview} disabled={busy}>
            Check & Suggest
          </Button>
          <Button className={classes.spaced} variant="contained" color="primary" onClick={this.save} disabled={busy}>
            {busy ? 'Saving…' : 'Save Scenario'}
          </Button>
          <Button className={classes.spaced} variant="contained" onClick={this.run} disabled={running || !editingId}>
            {running ? 'Running…' : 'Run Scenario'}
          </Button>
          <Button
            className={classes.spaced}
            onClick={() => this.setState({ editingId: null, results: null })}
            disabled={busy || running}
          >
            Close
          </Button>
        </div>
        {error && <Typography className={classes.error}>{error}</Typography>}
        {running && (
          <Typography className={classes.rationale}>
            Running every hypothetical campus through the full A-F pipeline; this can take a minute or two.
          </Typography>
        )}
        {this.renderResults()}
      </Paper>
    );
  }

  render() {
    const { classes } = this.props;
    const { scenarios, editingId, error } = this.state;
    const entries = Object.entries(scenarios || {}).sort((a, b) => (a[1].name || '').localeCompare(b[1].name || ''));
    return (
      <div className={classes.root}>
        <Typography variant="h5" gutterBottom>
          Reconfiguration Scenarios
        </Typography>
        <Typography className={classes.intro}>
          A scenario reassigns your tested grades into hypothetical campuses and projects each one's A-F report, plus
          the district rating under that arrangement next to today's.
        </Typography>
        {entries.map(([scenarioId, scenario]) => (
          <Paper key={scenarioId} className={classes.row}>
            <div>
              <Typography variant="subtitle1">{scenario.name}</Typography>
              <Typography variant="caption">
                {(scenario.members || []).map((m) => m.campusName || 'unnamed').join(', ')}
              </Typography>
            </div>
            <div>
              <IconButton onClick={() => this.openEdit(scenarioId, scenario)} title="Edit">
                <EditIcon fontSize="small" />
              </IconButton>
              <IconButton onClick={this.delete(scenarioId, scenario.name)} title="Delete">
                <DeleteIcon fontSize="small" />
              </IconButton>
            </div>
          </Paper>
        ))}
        {!entries.length && scenarios != null && <Typography className={classes.intro}>No scenarios yet.</Typography>}
        {editingId === null && (
          <Button variant="contained" color="primary" onClick={this.openNew}>
            New Scenario
          </Button>
        )}
        {this.renderEditor()}
        {editingId === null && error && <Typography className={classes.error}>{error}</Typography>}
      </div>
    );
  }
}

export default withStyles(styles)(ScenarioManager);
